<template>
  <div class="trend-bar-chart">
    <div class="chart-area">
      <canvas ref="chartCanvas"></canvas>
    </div>
    <div class="chart-legend">
      <span class="legend-item">
        <span class="legend-swatch" :style="{ backgroundColor: colors.blue[500] }"></span>
        {{ currentLabel }}
      </span>
      <span v-if="lastYearData.length > 0" class="legend-item">
        <span class="legend-swatch" :style="{ backgroundColor: colors.blue[400] }"></span>
        {{ previousLabel }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue';
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { colors } from '../../../../consts/colors';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const props = defineProps({
  labels: {
    type: Array,
    default: () => []
  },
  thisYearData: {
    type: Array,
    default: () => []
  },
  lastYearData: {
    type: Array,
    default: () => []
  },
  currentLabel: {
    type: String,
    default: ''
  },
  previousLabel: {
    type: String,
    default: ''
  }
});

const chartCanvas = ref(null);
let chartInstance = null;

// Shift the two same-width series apart by a quarter bar each, so the
// previous year peeks out half a bar behind the selected period, like
// the design. Runs after every update, on freshly computed positions.
const overlapBarsPlugin = {
  id: 'overlapBars',
  afterUpdate(chart) {
    const front = chart.getDatasetMeta(0);
    const back = chart.getDatasetMeta(1);
    if (!back || !back.data || back.data.length === 0) return;
    front.data.forEach((el) => { el.x -= el.width / 4; });
    back.data.forEach((el) => { el.x += el.width / 4; });
  }
};

const buildDatasets = () => {
  const datasets = [{
    label: props.currentLabel,
    data: props.thisYearData,
    backgroundColor: colors.blue[500],
    borderRadius: 4,
    grouped: false,
    order: 1,
    barPercentage: 0.6,
  }];

  if (props.lastYearData.length > 0) {
    datasets.push({
      label: props.previousLabel,
      data: props.lastYearData,
      backgroundColor: colors.blue[400],
      borderRadius: 4,
      grouped: false,
      order: 2,
      barPercentage: 0.6,
    });
  }

  return datasets;
};

const initializeChart = () => {
  if (!chartCanvas.value) return;

  const ctx = chartCanvas.value.getContext('2d');
  chartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: props.labels,
      datasets: buildDatasets()
    },
    plugins: [overlapBarsPlugin],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      // The overlap plugin repositions bars after update; animation would
      // override the shifted positions, so it stays off.
      animation: false,
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: colors.gray[500],
            maxTicksLimit: 12,
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: colors.gray[200]
          },
          ticks: {
            color: colors.gray[500],
            precision: 0
          }
        }
      }
    }
  });
};

const updateChart = () => {
  if (!chartInstance) {
    nextTick(() => initializeChart());
    return;
  }

  chartInstance.data.labels = props.labels;
  chartInstance.data.datasets = buildDatasets();
  chartInstance.update();
};

onMounted(() => {
  nextTick(() => initializeChart());
});

watch(() => [props.labels, props.thisYearData, props.lastYearData], () => {
  updateChart();
}, { deep: true });
</script>

<style lang="scss" scoped>
.trend-bar-chart {
  .chart-legend {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    font-size: 12px;
    color: v-bind('colors.gray[500]');
    margin-top: 8px;

    .legend-item {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .legend-swatch {
      width: 12px;
      height: 12px;
      border-radius: 2px;
      display: inline-block;
    }
  }

  .chart-area {
    height: 240px;
    position: relative;
  }
}
</style>
